// Home.tsx - listings + guest welcome hero + post-list highlight
import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import ProductCard from '../components/ProductCard';
import ProductGridSkeleton from '../components/ProductGridSkeleton';
import { getProducts, getPublicVersions, getFavourites, getSiteSettings } from '../services/api.shop';
import { Product, PaginatedResponse, SiteSettings } from '../types';
import { useFrontendAuth } from '../hooks/useAuth';
import CategoryFilter from '../components/CategoryFilter';
import ProductFilterSidebar, {
    emptyProductFilters,
    ProductFilterState,
    MobileFilterChip,
    MobileFilterFab,
    countActiveFilters,
} from '../components/ProductFilterSidebar';
import ProductResultsToolbar from '../components/ProductResultsToolbar';
// Phase 10b experiment: zero-listing only (no guest double Sell CTA — T210)
import SellerOnboardingBanner from '../components/SellerOnboardingBanner';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { translateWithFallback } from '../utils/translateWithFallback';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import AnimatedPetitoLogo from '../components/AnimatedPetitoLogo';
import {
    DEFAULT_PRODUCT_SORT,
    ProductSortKey,
    productSortApiValue,
} from '../utils/productSort';

type MobileFilterEntry = 'chip' | 'fab' | 'both';
const MOBILE_FILTER_ENTRY = 'fab' as MobileFilterEntry;

const DEFAULT_HERO_IMAGE =
    'https://images.unsplash.com/photo-1694605735529-8d60f23a30b6?ixlib=rb-4.1.0&q=80&w=1600&fm=jpg&crop=entropy&cs=tinysrgb&fit=max';

const Home = () => {
    const { user, isAuthReady, isAuthenticated } = useFrontendAuth();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    useDocumentTitle(tr('page.home.title', 'Home'));

    const [searchParams, setSearchParams] = useSearchParams();
    const highlightParam = searchParams.get('highlight');
    const highlightId = highlightParam ? parseInt(highlightParam, 10) : NaN;
    const [activeHighlightId, setActiveHighlightId] = useState<number | null>(
        !Number.isNaN(highlightId) && highlightId > 0 ? highlightId : null
    );

    const [selectedCategory, setSelectedCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState<ProductFilterState>(emptyProductFilters);
    const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
    const [sortKey, setSortKey] = useState<ProductSortKey>(DEFAULT_PRODUCT_SORT);
    /**
     * Fixed mobile filter FAB: only after scrolling past the guest welcome hero.
     * Do not use IntersectionObserver alone — hero height vs viewport often left the FAB on the hero.
     */
    const [showFilterFab, setShowFilterFab] = useState(false);
    const limit = 20;
    const productsAnchorRef = useRef<HTMLDivElement>(null);
    const welcomeHeroRef = useRef<HTMLDivElement>(null);

    const excludeSellerId =
        user?.excludeOwnProducts && user.id ? user.id : undefined;

    const queryParams = useMemo(
        () => ({
            page: currentPage,
            limit,
            filter: productSortApiValue(sortKey),
            category: selectedCategory === 'all' ? undefined : selectedCategory,
            minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
            maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
            conditions: filters.conditions.length > 0 ? filters.conditions.map((c) => String(c)) : undefined,
            specFilters: filters.specFilters.length > 0 ? filters.specFilters : undefined,
            growthPhaseIds: filters.growthPhaseIds.length > 0 ? filters.growthPhaseIds : undefined,
            ageMinMonths:
                filters.growthPhaseIds.length === 0 && filters.ageMinMonths != null
                    ? filters.ageMinMonths
                    : undefined,
            ageMaxMonths:
                filters.growthPhaseIds.length === 0 && filters.ageMaxMonths != null
                    ? filters.ageMaxMonths
                    : undefined,
            excludeSellerId,
        }),
        [currentPage, selectedCategory, filters, limit, sortKey, excludeSellerId]
    );

    // Viewer id scopes reservedByMe so User A/B never share the same product cache entry
    const viewerKey = user?.id ?? 'anon';

    const { data, isLoading: loading, error } = useQuery<PaginatedResponse<Product>>({
        queryKey: ['products', queryParams, viewerKey],
        queryFn: () => getProducts(queryParams),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: false,
    });

    const { data: favourites = [] } = useProtectedQuery({
        queryKey: ['favourites'],
        queryFn: getFavourites,
        staleTime: 10 * 60 * 1000,
    });

    const { data: siteSettings } = useQuery<SiteSettings>({
        queryKey: ['siteSettings'],
        queryFn: getSiteSettings,
        staleTime: 5 * 60 * 1000,
    });

    const queryClient = useQueryClient();

    const { data: versions } = useQuery({
        queryKey: ['publicVersions'],
        queryFn: getPublicVersions,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
        refetchOnWindowFocus: true,
    });

    const prevProductsVersion = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (
            versions?.products &&
            prevProductsVersion.current !== undefined &&
            prevProductsVersion.current !== versions.products
        ) {
            queryClient.invalidateQueries({ queryKey: ['products'] });
        }
        prevProductsVersion.current = versions?.products;
    }, [versions?.products, queryClient]);

    // Post-list pulse: scroll into view and clear query after a few seconds
    useEffect(() => {
        if (!activeHighlightId) return;
        const timer = window.setTimeout(() => {
            setActiveHighlightId(null);
            if (searchParams.has('highlight')) {
                const next = new URLSearchParams(searchParams);
                next.delete('highlight');
                setSearchParams(next, { replace: true });
            }
        }, 5000);
        return () => window.clearTimeout(timer);
    }, [activeHighlightId, searchParams, setSearchParams]);

    useEffect(() => {
        if (!activeHighlightId || loading) return;
        const el = document.querySelector(`[data-product-id="${activeHighlightId}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [activeHighlightId, loading, data]);

    const rawProducts: Product[] = data?.data && Array.isArray(data.data) ? data.data : [];
    const products = rawProducts;
    const total = data?.total ?? 0;
    const totalPages = total ? Math.ceil(total / limit) : 1;

    const showWelcome = isAuthReady && !isAuthenticated;
    const heroImage = siteSettings?.welcomeHeroImageUrl?.trim() || DEFAULT_HERO_IMAGE;
    // Default to visual how-it-works; admin WelcomeHowItWorksPath can still override
    const howPath = siteSettings?.welcomeHowItWorksPath?.trim() || '/how-it-works';

    // Mobile filter FAB: never over welcome; only after scroll past hero bottom
    useEffect(() => {
        if (!showWelcome) {
            setShowFilterFab(true);
            return;
        }

        setShowFilterFab(false);

        const updateFabVisibility = () => {
            const hero = welcomeHeroRef.current;
            if (!hero) {
                setShowFilterFab(false);
                return;
            }
            // Hero bottom relative to viewport — show FAB only when hero is mostly off-screen
            const rect = hero.getBoundingClientRect();
            const pastHero = rect.bottom < window.innerHeight * 0.35;
            setShowFilterFab(pastHero);
        };

        updateFabVisibility();
        window.addEventListener('scroll', updateFabVisibility, { passive: true });
        window.addEventListener('resize', updateFabVisibility);
        return () => {
            window.removeEventListener('scroll', updateFabVisibility);
            window.removeEventListener('resize', updateFabVisibility);
        };
    }, [showWelcome]);

    const scrollToListings = () => {
        productsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleFiltersChange = (next: ProductFilterState) => {
        setFilters(next);
        setCurrentPage(1);
    };

    const handleSortChange = (key: ProductSortKey) => {
        setSortKey(key);
        setCurrentPage(1);
    };

    if (error) {
        return <div className="text-center text-red-500 py-8">{t('common.error')}</div>;
    }

    // Avoid product-grid flash before welcome: hold a cream shell until /auth/me resolves.
    // (Guests used to see listings for a beat, then the banner — worse after boot splash.)
    if (!isAuthReady) {
        return (
            <div
                className="flex min-h-[calc(100dvh-var(--shop-header-height,7rem)-var(--mobile-bottom-nav-height,4rem))] flex-col items-center justify-center bg-[var(--bg-color,#F7F3EB)] px-4"
                role="status"
                aria-live="polite"
                aria-busy="true"
            >
                <AnimatedPetitoLogo size="md" mode="css" playful decorative />
                <p className="mt-4 text-base font-medium text-[var(--text-color)]/80">
                    {tr('state.loading', 'Loading')}
                </p>
            </div>
        );
    }

    return (
        <>
            {/*
              Full-bleed guest hero (Figma mobile + desktop):
              - Mobile: full remaining viewport, centered-left CTAs, right edge My Journey tab
              - Space for bottom nav; “Browse items below” above it
            */}
            {showWelcome && (
                <div
                    ref={welcomeHeroRef}
                    className="relative left-1/2 z-10 w-screen max-w-[100vw] -translate-x-1/2 overflow-hidden
                        h-[calc(100dvh-var(--shop-header-height,7rem)-var(--mobile-bottom-nav-height,4rem))]
                        min-h-[min(72dvh,520px)]
                        max-h-[900px]
                        lg:h-[calc(100dvh-var(--shop-header-height,5.5rem))]
                        lg:min-h-0
                        lg:max-h-none"
                >
                    <img
                        src={heroImage}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover object-[center_28%] sm:object-center"
                    />
                    {/* Mobile: soft vignette so face stays visible; desktop: left gradient for CTAs */}
                    <div className="absolute inset-0 bg-black/40 lg:bg-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30 lg:from-black/40 lg:via-transparent lg:to-transparent" />
                    <div className="absolute inset-0 hidden bg-gradient-to-r from-black/70 via-black/30 to-transparent lg:block" />

                    {/* Main CTA stack — Figma: mid-left; secondary CTA is clear frosted glass pill */}
                    <div className="relative z-10 flex h-full flex-col justify-center px-5 pb-16 pt-4 sm:px-8 lg:px-10">
                        <div className="mx-auto w-full max-w-7xl lg:mx-0">
                            <div className="w-full max-w-[22rem] sm:max-w-md lg:max-w-lg">
                                <div className="mb-6 flex flex-col items-start gap-3 text-white drop-shadow-md sm:mb-8 sm:gap-4">
                                    {/* Welcome mark: compact on phone (~⅓ smaller again); Figma-scale from sm up. Text sizes untouched. */}
                                    <span className="flex h-[min(132px,36vw)] w-[min(132px,36vw)] shrink-0 items-center justify-center rounded-full bg-white/95 p-[6%] shadow-md ring-1 ring-white/40 sm:h-[280px] sm:w-[280px] sm:p-[5%] lg:h-[350px] lg:w-[350px]">
                                        <AnimatedPetitoLogo size="lg" mode="css" playful decorative />
                                    </span>
                                    <span className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
                                        Petito
                                    </span>
                                </div>

                                <Link
                                    to="/register"
                                    className="mb-3 flex min-h-[3.25rem] w-full items-center justify-center gap-2 rounded-full bg-[var(--accent-color)] px-5 py-3.5 text-base font-semibold !text-white shadow-lg transition hover:brightness-95 active:scale-[0.99]"
                                >
                                    <span className="!text-white">
                                        {tr('welcome.cta.create', 'Create free account')}
                                    </span>
                                    <span aria-hidden className="text-lg leading-none !text-white">
                                        →
                                    </span>
                                </Link>

                                {/* Figma secondary: white ring + frosted dark fill; force white text (global a color used to override) */}
                                <Link
                                    to={howPath.startsWith('http') ? howPath : howPath}
                                    className="mb-5 flex min-h-[3.25rem] w-full items-center justify-center gap-2 rounded-full border-2 border-white bg-black/50 px-5 py-3.5 text-base font-semibold !text-white shadow-[0_4px_20px_rgba(0,0,0,0.4)] backdrop-blur-md transition hover:bg-black/60 active:scale-[0.99]"
                                >
                                    <span className="!text-white">
                                        {tr('welcome.cta.how', 'How does it work?')}
                                    </span>
                                    <span aria-hidden className="text-lg leading-none !text-white">
                                        →
                                    </span>
                                </Link>

                                <p className="text-sm !text-white drop-shadow-md">
                                    {tr('welcome.login.prefix', 'Already have an account?')}{' '}
                                    <Link
                                        to="/login"
                                        className="font-semibold !text-[var(--accent-color)] underline decoration-white/50 underline-offset-2 hover:brightness-110"
                                    >
                                        {tr('welcome.login', 'Log in')}
                                    </Link>
                                </p>
                                <button
                                    type="button"
                                    onClick={scrollToListings}
                                    className="mt-2 min-h-11 text-left text-sm font-medium !text-white/90 underline-offset-2 drop-shadow transition hover:!text-white hover:underline"
                                >
                                    {tr('welcome.browseWithout', 'Browse without account')} →
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Scroll hint — above bottom nav on mobile */}
                    <button
                        type="button"
                        onClick={scrollToListings}
                        className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-0.5 text-white/95 drop-shadow-md transition hover:text-white
                            max-lg:bottom-[0.5rem]
                            lg:bottom-6"
                        aria-label={tr('welcome.browse', 'Browse items below')}
                    >
                        <span className="text-[11px] font-medium sm:text-xs">
                            {tr('welcome.browse', 'Browse items below')}
                        </span>
                        <ChevronDown
                            className="h-5 w-5 motion-safe:animate-bounce sm:h-6 sm:w-6 max-lg:motion-safe:[animation-iteration-count:6]"
                            aria-hidden
                        />
                    </button>
                </div>
            )}

            {/* T209: My Journey edge control — logged-in only (guest hero tab removed, ticket #255) */}
            {isAuthReady && isAuthenticated && (
                <Link
                    to="/my-petito"
                    data-tour-id="home.journey"
                    className="fixed right-0 top-1/2 z-30 flex -translate-y-1/2 flex-col items-center gap-1 rounded-l-xl border border-r-0 border-[color-mix(in_srgb,var(--accent-color),white_20%)] bg-[var(--accent-color)] px-2 py-3 text-white shadow-lg transition hover:brightness-95 active:scale-[0.98] sm:px-2.5 sm:py-4"
                    aria-label={tr('welcome.journey', 'My Journey')}
                >
                    <span className="text-base leading-none sm:text-lg" aria-hidden>
                        🌱
                    </span>
                    <span
                        className="select-none text-[10px] font-semibold leading-tight tracking-wide sm:text-[11px]"
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                        {tr('welcome.journey', 'My Journey')}
                    </span>
                    <span className="text-[10px] opacity-70" aria-hidden>
                        ‹
                    </span>
                </Link>
            )}

            <div ref={productsAnchorRef} className="px-4 sm:px-0">
                {/* T210: SellerOnboardingBanner — double CTA with header Sell; keep component for later
                <SellerOnboardingBanner />
                */}

                {/* Phase 7: calm guest trust bar (no fake counters) */}
                {showWelcome && (
                    <p className="mx-auto max-w-[90rem] px-4 pt-4 text-center text-xs leading-relaxed text-[color-mix(in_srgb,var(--text-color),transparent_35%)] sm:text-sm">
                        {tr(
                            'home.trust.bar',
                            'Belgian parents · Pre-loved baby gear · Community rules'
                        )}
                    </p>
                )}

                {/* Phase 10b: sell prompt only when logged-in with zero listings */}
                {isAuthenticated && (
                    <div className="mx-auto w-full max-w-[90rem] px-4 pt-4 sm:px-0">
                        <SellerOnboardingBanner />
                    </div>
                )}

                <CategoryFilter
                    selectedCategory={selectedCategory}
                    onCategoryChange={(cat) => {
                        setSelectedCategory(cat);
                        setCurrentPage(1);
                    }}
                    leadingSlot={
                        MOBILE_FILTER_ENTRY === 'chip' || MOBILE_FILTER_ENTRY === 'both' ? (
                            <MobileFilterChip
                                activeCount={countActiveFilters(filters)}
                                isOpen={filtersDrawerOpen}
                                onClick={() => setFiltersDrawerOpen(true)}
                            />
                        ) : undefined
                    }
                />

                <div className="mx-auto flex w-full max-w-[90rem] gap-8 py-6 sm:py-8">
                    <ProductFilterSidebar
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        mobileOpen={filtersDrawerOpen}
                        onMobileOpenChange={setFiltersDrawerOpen}
                    />

                    {(MOBILE_FILTER_ENTRY === 'fab' || MOBILE_FILTER_ENTRY === 'both') &&
                        showFilterFab && (
                        <MobileFilterFab
                            activeCount={countActiveFilters(filters)}
                            isOpen={filtersDrawerOpen}
                            onClick={() => setFiltersDrawerOpen(true)}
                        />
                    )}

                    <main className="min-w-0 flex-1">
                        {!loading && (
                            <ProductResultsToolbar
                                count={total}
                                sort={sortKey}
                                onSortChange={handleSortChange}
                                showCount={true}
                            />
                        )}

                        {loading ? (
                            <ProductGridSkeleton count={8} />
                        ) : products.length === 0 ? (
                            <p className="py-12 text-center text-[var(--text-color)]">{t('home.noProducts')}</p>
                        ) : (
                            <>
                                <div className="product-grid">
                                    {products.map((product) => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            showPrice={!!user}
                                            hideOwnBadge={false}
                                            favourites={favourites}
                                            highlight={activeHighlightId === product.id}
                                        />
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <nav className="mt-8 flex justify-center" aria-label="Pagination">
                                        <div className="inline-flex items-center gap-1 rounded-2xl border border-[var(--accent-color)]/30 bg-[var(--bg-color)] p-1 shadow-sm">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-[var(--text-color)] transition-all hover:bg-[var(--accent-color)]/10 active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-40"
                                                aria-label={t('common.previous')}
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                                <span>{t('common.previous')}</span>
                                            </button>

                                            <div className="mx-0.5 select-none rounded-xl bg-[var(--accent-color)] px-4 py-2 text-sm font-semibold text-white shadow-sm">
                                                {currentPage}{' '}
                                                <span className="font-normal opacity-75">/ {totalPages}</span>
                                            </div>

                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-[var(--text-color)] transition-all hover:bg-[var(--accent-color)]/10 active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-40"
                                                aria-label={t('common.next')}
                                            >
                                                <span>{t('common.next')}</span>
                                                <ChevronRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </nav>
                                )}
                            </>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
};

export default Home;
